import { refKeyDeVinculacion } from "./lineasFacturacion/refKey";
import { derivarClaveHardware, resolverLinea } from "./lineasFacturacion/resolverLinea";
import { log } from "./log";
import { client } from "./supabase";

/**
 * Dado un array de facturas_sii_emitidas vinculadas,
 * devuelve un Map<key, {plazocobro, facturafechacobro}>. Pauta/BTL/contratos: solo la key legacy
 * (`venta_tipo-venta_ref_id-venta_ref_index`). Cartelería/hardware: L6 tramo 4 emite AMBAS keys —
 * la legacy (la sigue leyendo `FlujoCajaV2/index.js`, que no se toca este tramo) Y la nueva
 * (`refKeyDeVinculacion`, `linea:<linea_id>`) para los consumidores ya migrados. La legacy se retira
 * en el tramo 4b, cuando FlujoCajaV2 pueda migrar a la key nueva.
 */
export const getPlazosCobroParaSII = async (siiRows) => {
  const result = new Map();
  if (!siiRows?.length) return result;

  const makeKey = (tipo, id, index) => `${tipo}-${id}-${index ?? ""}`;

  const porTipo = {};
  for (const f of siiRows) {
    if (!f.venta_tipo || !f.venta_ref_id) continue;
    if (!porTipo[f.venta_tipo]) porTipo[f.venta_tipo] = [];
    porTipo[f.venta_tipo].push(f);
  }

  if (porTipo.sales?.length) {
    const ids = [...new Set(porTipo.sales.map((f) => f.venta_ref_id))];
    const { data } = await client.from("sales").select("id, plazocobro, facturafechacobro, moneda_dolar").in("id", ids);
    for (const row of data || []) {
      for (const f of porTipo.sales.filter((x) => x.venta_ref_id === row.id)) {
        result.set(makeKey("sales", row.id, f.venta_ref_index), {
          plazocobro: row.plazocobro ?? null,
          facturafechacobro: row.facturafechacobro ?? null,
          moneda_dolar_origen: row.moneda_dolar ?? false,
        });
      }
    }
  }

  if (porTipo.sales_btl?.length) {
    const ids = [...new Set(porTipo.sales_btl.map((f) => f.venta_ref_id))];
    const { data } = await client.from("sales_btl").select("id, facturafechacobro").in("id", ids);
    for (const row of data || []) {
      for (const f of porTipo.sales_btl.filter((x) => x.venta_ref_id === row.id)) {
        result.set(makeKey("sales_btl", row.id, f.venta_ref_index), {
          plazocobro: null,
          facturafechacobro: row.facturafechacobro ?? null,
          moneda_dolar_origen: false,
        });
      }
    }
  }

  if (porTipo.sales_contratos?.length) {
    const ids = [...new Set(porTipo.sales_contratos.map((f) => f.venta_ref_id))];
    const { data } = await client.from("sales_contratos").select("id, meses, moneda_dolar").in("id", ids);
    for (const row of data || []) {
      for (const f of porTipo.sales_contratos.filter((x) => x.venta_ref_id === row.id)) {
        const mes = f.venta_ref_index != null ? (row.meses || [])[f.venta_ref_index] : null;
        result.set(makeKey("sales_contratos", row.id, f.venta_ref_index), {
          plazocobro: mes?.plazocobro ?? null,
          facturafechacobro: mes?.facturafechacobro ?? null,
          moneda_dolar_origen: row.moneda_dolar ?? false,
        });
      }
    }
  }

  // Cartelería: los dos venta_tipo comparten origen (negocios_carteleria) y el mismo lookup por
  // folio. 'negocios_carteleria' = gestión/OPEX (índice de meses_facturacion); 'negocios_carteleria_hw'
  // = HW-CAPEX (índice de detalle_pagos_hardware, Variante 1). El match es por folio en ambos
  // JSONB, así que un solo recorrido sirve; solo el TIPO de la key cambia (f.venta_tipo).
  const cartRows = [...(porTipo.negocios_carteleria || []), ...(porTipo.negocios_carteleria_hw || [])];
  if (cartRows.length > 0) {
    const ids = [...new Set(cartRows.map((f) => f.venta_ref_id))];
    const [negociosRes, lineasRes] = await Promise.all([
      client.from("negocios_carteleria").select("id, meses_facturacion, detalle_pagos_hardware").in("id", ids),
      // lineas_facturacion (L1, derivada por trigger): resuelve linea_id por clave natural para emitir
      // la key nueva (refKeyDeVinculacion) junto a la legacy — L6 tramo 4.
      client
        .from("lineas_facturacion")
        .select("id, negocio_id, tipo, ano, mes, tramo")
        .in("negocio_id", ids.map(String)),
    ]);
    const lineasPorNegocio = new Map();
    (lineasRes.data || []).forEach((l) => {
      const k = String(l.negocio_id);
      if (!lineasPorNegocio.has(k)) lineasPorNegocio.set(k, []);
      lineasPorNegocio.get(k).push(l);
    });

    for (const row of negociosRes.data || []) {
      for (const f of cartRows.filter((x) => x.venta_ref_id === row.id)) {
        const folio = String(f.folio);
        const legacyKey = makeKey(f.venta_tipo, row.id, f.venta_ref_index);
        const emitirNueva = (linea, contexto) => {
          if (linea) {
            result.set(
              refKeyDeVinculacion({ venta_tipo: f.venta_tipo, venta_ref_id: row.id, linea_id: linea.id }),
              result.get(legacyKey)
            );
          } else {
            log.error("plazosCobroSII.linea_id_no_resuelto", { negocio_id: row.id, folio, ...contexto });
          }
        };
        const meses =
          typeof row.meses_facturacion === "string"
            ? JSON.parse(row.meses_facturacion || "[]")
            : row.meses_facturacion || [];
        const entryMesIdx = meses.findIndex((m) => String(m.facturanumero) === folio);
        if (entryMesIdx !== -1) {
          const entryMes = meses[entryMesIdx];
          result.set(legacyKey, {
            plazocobro: entryMes.plazocobro ?? null,
            facturafechacobro: entryMes.facturafechacobro ?? null,
            moneda_dolar_origen: false,
          });
          const linea = resolverLinea(lineasPorNegocio.get(String(row.id)) || [], {
            tipo: entryMes.tipo,
            ano: entryMes.ano,
            mes: entryMes.mes,
            tramo: 1,
          });
          emitirNueva(linea, { tipo: entryMes.tipo, ano: entryMes.ano, mes: entryMes.mes });
          continue;
        }
        const detalle =
          typeof row.detalle_pagos_hardware === "string"
            ? JSON.parse(row.detalle_pagos_hardware || "[]")
            : row.detalle_pagos_hardware || [];
        const entryHwIdx = detalle.findIndex((d) => String(d.facturanumero) === folio);
        const entryHw = entryHwIdx !== -1 ? detalle[entryHwIdx] : null;
        result.set(legacyKey, {
          plazocobro: entryHw?.plazocobro ?? null,
          facturafechacobro: entryHw?.facturafechacobro ?? null,
          moneda_dolar_origen: false,
        });
        if (entryHwIdx !== -1) {
          const clave = derivarClaveHardware(detalle, entryHwIdx);
          const linea = clave ? resolverLinea(lineasPorNegocio.get(String(row.id)) || [], clave) : null;
          emitirNueva(linea, clave || { motivo: "tramo_no_datable" });
        }
      }
    }
  }

  return result;
};
